// Change #100326 - Settings > WhatsApp > Templates.
//
// A template is built in Twilio's Content API and submitted to WhatsApp (Meta) for approval when
// it is created here; "Sync from Twilio" pulls every template and its approval status back,
// including ones made in the Twilio console. After submission only the local bits can change:
// the name, the merge-field mapping and whether staff can pick it.
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Col, Input, Label, Row, Table } from "reactstrap";
import { toast } from "react-toastify";

import AppModal from "../../../../Components/Common/AppModal";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import {
  createTemplate,
  deleteTemplate,
  errorText,
  listTemplates,
  ok,
  syncTemplates,
  updateTemplate,
} from "../../../../Components/WhatsApp/whatsappApi";
import { placeholdersIn, renderTemplate } from "../../../../Components/WhatsApp/whatsappUtils";
import { DateCell, FieldHelp, ListState, Switch, Toolbar, channelName, isOn, parseJson } from "./shared";

export const CATEGORIES = {
  UTILITY: "Updates about something the customer already asked for - bookings, reminders, invoices.",
  MARKETING: "Offers, promotions and news. Needs the customer's opt-in and costs more per message.",
  AUTHENTICATION: "One-time passcodes only.",
};

const LANGUAGES = [
  ["en_GB", "English (UK)"],
  ["en_US", "English (US)"],
  ["en", "English"],
  ["fr", "French"],
  ["de", "German"],
  ["es", "Spanish"],
];

export const approvalMeta = (status) => {
  const s = String(status || "").toLowerCase();
  if (!s) return { label: "Not checked", cls: "bg-secondary-subtle text-secondary" };
  if (s === "approved") return { label: "Approved", cls: "bg-success-subtle text-success" };
  if (s === "pending" || s === "received") return { label: s === "received" ? "Received" : "Pending", cls: "bg-warning-subtle text-warning" };
  if (s === "rejected") return { label: "Rejected", cls: "bg-danger-subtle text-danger" };
  return { label: s.replace(/_/g, " "), cls: "bg-secondary-subtle text-secondary" };
};

/** Approved, or never checked (templates added by hand before #100326). */
export const isSendable = (t) => {
  const s = String(t.ApprovalStatus || "").toLowerCase();
  return !s || s === "approved";
};

const ApprovalBadge = ({ template }) => {
  const meta = approvalMeta(template.ApprovalStatus);
  const rejected = String(template.ApprovalStatus || "").toLowerCase() === "rejected";
  return (
    <>
      <span className={`badge ${meta.cls} text-capitalize`} title={rejected ? template.ApprovalRejectedReason || "" : undefined}>
        {meta.label}
      </span>
      {rejected && template.ApprovalRejectedReason && (
        <div className="small text-danger mt-1 wa-settings__reason">{template.ApprovalRejectedReason}</div>
      )}
    </>
  );
};

ApprovalBadge.propTypes = { template: PropTypes.object.isRequired };

/** Client-side copy of the backend rules, so the user hears about them before Twilio does. */
const bodyProblem = (body, placeholders) => {
  const text = body.trim();
  if (!text) return null;
  if (text.length > 1024) return "WhatsApp template bodies are limited to 1024 characters.";
  const expected = placeholders.map((_, i) => String(i + 1));
  if (placeholders.join(",") !== expected.join(",")) return "Number the placeholders in order: {{1}}, {{2}}, {{3}}...";
  if (/^\s*{{\s*\d+\s*}}/.test(text) || /{{\s*\d+\s*}}\s*$/.test(text)) {
    return "WhatsApp rejects templates that start or end with a placeholder - add some words around it.";
  }
  return null;
};

const MergeFieldSelect = ({ id, value, onChange, mergeFields }) => (
  <Input id={id} type="select" bsSize="sm" value={value || ""} onChange={(e) => onChange(e.target.value)}>
    <option value="">Staff type it when sending</option>
    {Object.entries(mergeFields).map(([k, label]) => (
      <option key={k} value={k}>
        {label}
      </option>
    ))}
  </Input>
);

MergeFieldSelect.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  mergeFields: PropTypes.object.isRequired,
};

const Preview = ({ body, samples }) => (
  <div className="wa-settings__preview">
    <div className="wa-settings__preview-label">Preview</div>
    <div className="wa-settings__preview-chat">
      <div className="wa-msg wa-msg--out">
        {body.trim() ? renderTemplate(body, samples) : <span className="text-muted">Your message appears here</span>}
      </div>
    </div>
  </div>
);

Preview.propTypes = { body: PropTypes.string.isRequired, samples: PropTypes.object.isRequired };

const NewTemplateModal = ({ open, channels, mergeFields, onClose, onSaved }) => {
  const blank = () => ({
    MessagingChannel_Id: channels[0] ? String(channels[0].Id) : "",
    Name: "",
    Category: "UTILITY",
    Language: "en_GB",
    Message: "",
    samples: {},
    map: {},
  });
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(blank());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const placeholders = useMemo(() => placeholdersIn(form.Message), [form.Message]);
  const problem = bodyProblem(form.Message, placeholders);
  const missingSample = placeholders.some((p) => !String(form.samples[p] || "").trim());
  const canSave =
    form.MessagingChannel_Id && form.Name.trim() && form.Message.trim() && !problem && !missingSample && !saving;

  const save = async () => {
    if (!canSave) return;
    const samples = {};
    const map = {};
    placeholders.forEach((p) => {
      samples[p] = String(form.samples[p]).trim();
      if (form.map[p]) map[p] = form.map[p];
    });
    setSaving(true);
    const res = await createTemplate({
      MessagingChannel_Id: form.MessagingChannel_Id,
      Name: form.Name.trim(),
      Category: form.Category,
      Language: form.Language,
      Message: form.Message.trim(),
      samples,
      VariableMapJson: Object.keys(map).length ? map : null,
    });
    setSaving(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not create the template"));
      return;
    }
    toast.success(res.message || "Template submitted");
    if (res.warning) toast.warn(res.warning);
    onSaved();
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="New WhatsApp template"
      subtitle="Submitted to WhatsApp for approval when you save"
      icon="ri-file-text-line"
      headerAccent="emerald"
      size="lg"
      loading={saving}
      onSubmit={save}
      submitLabel="Submit for approval"
      submitDisabled={!canSave}
    >
      <Row className="g-3">
        <Col lg={7}>
          <Row className="g-3">
            <Col md={6}>
              <Label htmlFor="wa-tpl-channel">Channel</Label>
              <Input
                id="wa-tpl-channel"
                type="select"
                value={form.MessagingChannel_Id}
                onChange={(e) => set("MessagingChannel_Id", e.target.value)}
              >
                {!channels.length && <option value="">Add a channel first</option>}
                {channels.map((c) => (
                  <option key={c.Id} value={String(c.Id)}>
                    {channelName(c)}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={6}>
              <Label htmlFor="wa-tpl-name">Name</Label>
              <Input
                id="wa-tpl-name"
                value={form.Name}
                onChange={(e) => set("Name", e.target.value)}
                placeholder="e.g. Appointment reminder"
              />
            </Col>
            <Col md={6}>
              <Label htmlFor="wa-tpl-cat">Category</Label>
              <Input id="wa-tpl-cat" type="select" value={form.Category} onChange={(e) => set("Category", e.target.value)}>
                {Object.keys(CATEGORIES).map((c) => (
                  <option key={c} value={c}>
                    {c.charAt(0) + c.slice(1).toLowerCase()}
                  </option>
                ))}
              </Input>
              <FieldHelp>{CATEGORIES[form.Category]}</FieldHelp>
            </Col>
            <Col md={6}>
              <Label htmlFor="wa-tpl-lang">Language</Label>
              <Input id="wa-tpl-lang" type="select" value={form.Language} onChange={(e) => set("Language", e.target.value)}>
                {LANGUAGES.map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col xs={12}>
              <Label htmlFor="wa-tpl-body">Message</Label>
              <Input
                id="wa-tpl-body"
                type="textarea"
                rows={6}
                value={form.Message}
                onChange={(e) => set("Message", e.target.value)}
                placeholder="Hi {{1}}, this is a reminder that your appointment is on {{2}}. Reply here if you need to change it."
                invalid={!!problem}
              />
              <div className="d-flex justify-content-between">
                <FieldHelp>Use {"{{1}}"}, {"{{2}}"} for values, numbered in order. Do not start or end with a placeholder.</FieldHelp>
                <span className={`form-text ${form.Message.length > 1024 ? "text-danger" : ""}`}>{form.Message.length}/1024</span>
              </div>
              {problem && <div className="text-danger small">{problem}</div>}
            </Col>
          </Row>

          {placeholders.length > 0 && (
            <>
              <div className="wa-settings__section-title mt-4">Values</div>
              <p className="text-muted small mb-2">
                WhatsApp needs a sample for each value to review the template. Pick a field to fill it in automatically
                when the template is sent from a record.
              </p>
              {placeholders.map((p) => (
                <Row key={p} className="g-2 align-items-center mb-2">
                  <Col xs="auto">
                    <span className="badge bg-primary-subtle text-primary wa-settings__ph">{`{{${p}}}`}</span>
                  </Col>
                  <Col>
                    <Input
                      bsSize="sm"
                      aria-label={`Sample value for {{${p}}}`}
                      placeholder="Sample value (required)"
                      value={form.samples[p] || ""}
                      invalid={!String(form.samples[p] || "").trim()}
                      onChange={(e) => set("samples", { ...form.samples, [p]: e.target.value })}
                    />
                  </Col>
                  <Col>
                    <MergeFieldSelect
                      id={`wa-tpl-map-${p}`}
                      value={form.map[p]}
                      mergeFields={mergeFields}
                      onChange={(v) => set("map", { ...form.map, [p]: v })}
                    />
                  </Col>
                </Row>
              ))}
            </>
          )}
        </Col>
        <Col lg={5}>
          <Preview body={form.Message} samples={form.samples} />
        </Col>
      </Row>
    </AppModal>
  );
};

NewTemplateModal.propTypes = {
  open: PropTypes.bool,
  channels: PropTypes.array.isRequired,
  mergeFields: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

const EditTemplateModal = ({ row, mergeFields, onClose, onSaved }) => {
  const [form, setForm] = useState({ Name: "", map: {}, FlagActive: 1 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (row) {
      setForm({
        Name: row.Name || "",
        map: parseJson(row.VariableMapJson, {}) || {},
        FlagActive: row.FlagActive === null || row.FlagActive === undefined || isOn(row.FlagActive) ? 1 : 0,
      });
    }
  }, [row]);

  const placeholders = row ? row.placeholder_list || placeholdersIn(row.Message) : [];

  const save = async () => {
    if (!form.Name.trim()) return;
    const map = {};
    placeholders.forEach((p) => {
      if (form.map[p]) map[p] = form.map[p];
    });
    setSaving(true);
    const res = await updateTemplate({
      Id: row.Id,
      Name: form.Name.trim(),
      VariableMapJson: Object.keys(map).length ? map : null,
      FlagActive: form.FlagActive,
    });
    setSaving(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not save the template"));
      return;
    }
    toast.success(res.message || "Saved");
    onSaved();
  };

  return (
    <AppModal
      open={!!row}
      onClose={onClose}
      title="Edit template"
      subtitle={row ? row.Name : ""}
      icon="ri-file-text-line"
      headerAccent="emerald"
      size="md"
      loading={saving}
      onSubmit={save}
      submitDisabled={!form.Name.trim() || saving}
    >
      {row && (
        <>
          <div className="wa-settings__callout mb-3 small">
            <i className="ri-information-line me-1" />
            The wording can't change once a template has been submitted to WhatsApp. To change it, create a new
            template and switch this one off.
          </div>
          <Label htmlFor="wa-tpl-ename">Name</Label>
          <Input id="wa-tpl-ename" value={form.Name} onChange={(e) => setForm((f) => ({ ...f, Name: e.target.value }))} />
          <FieldHelp>Only changes the name staff see here - not the name in Twilio.</FieldHelp>

          <div className="wa-settings__preview-chat mt-3">
            <div className="wa-msg wa-msg--out">{row.Message}</div>
          </div>

          {placeholders.length > 0 && (
            <>
              <div className="wa-settings__section-title mt-4">Fill values automatically from</div>
              {placeholders.map((p) => (
                <Row key={p} className="g-2 align-items-center mb-2">
                  <Col xs="auto">
                    <span className="badge bg-primary-subtle text-primary wa-settings__ph">{`{{${p}}}`}</span>
                  </Col>
                  <Col>
                    <MergeFieldSelect
                      id={`wa-tpl-emap-${p}`}
                      value={form.map[p]}
                      mergeFields={mergeFields}
                      onChange={(v) => setForm((f) => ({ ...f, map: { ...f.map, [p]: v } }))}
                    />
                  </Col>
                </Row>
              ))}
            </>
          )}

          <div className="mt-3">
            <Switch
              id="wa-tpl-eactive"
              checked={isOn(form.FlagActive)}
              onChange={(v) => setForm((f) => ({ ...f, FlagActive: v }))}
              label="Active - staff and notifications can use it"
            />
          </div>
        </>
      )}
    </AppModal>
  );
};

EditTemplateModal.propTypes = {
  row: PropTypes.object,
  mergeFields: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

const TemplatesTab = ({ channels }) => {
  const [rows, setRows] = useState([]);
  const [mergeFields, setMergeFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const load = async () => {
    setLoading(true);
    const res = await listTemplates();
    setLoading(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not load templates"));
      return;
    }
    setRows(res.messagingconversationtemplate_list || []);
    setMergeFields(res.merge_field_list || {});
  };

  useEffect(() => {
    load();
  }, []);

  const channelById = (id) => channels.find((c) => String(c.Id) === String(id));

  const sync = async () => {
    setSyncing(true);
    const res = await syncTemplates();
    setSyncing(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not sync templates"));
      return;
    }
    const errors = res.sync_summary?.errors || [];
    if (errors.length) toast.warn(`${res.message}. ${errors.join("; ")}`);
    else toast.success(res.message || "Templates synced");
    load();
  };

  const toggleActive = async (row, value) => {
    setTogglingId(row.Id);
    const res = await updateTemplate({ Id: row.Id, FlagActive: value });
    setTogglingId(null);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not update the template"));
      return;
    }
    setRows((list) => list.map((r) => (r.Id === row.Id ? { ...r, FlagActive: value } : r)));
  };

  const confirmDelete = async () => {
    setDeleteBusy(true);
    const res = await deleteTemplate(deleting.Id);
    setDeleteBusy(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not delete the template"));
      return;
    }
    toast.success(res.message || "Deleted");
    setDeleting(null);
    load();
  };

  const newButton = (
    <Button color="primary" size="sm" onClick={() => setCreating(true)} disabled={!channels.length}>
      <i className="ri-add-line me-1" />
      New template
    </Button>
  );

  return (
    <>
      <Toolbar note="WhatsApp only lets you start a conversation (or message after 24 hours) with an approved template.">
        <Button color="light" size="sm" onClick={sync} disabled={syncing || !channels.length}>
          {syncing ? (
            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
          ) : (
            <i className="ri-refresh-line me-1" />
          )}
          Sync from Twilio
        </Button>
        {newButton}
      </Toolbar>

      <ListState
        loading={loading && !rows.length}
        empty={!rows.length}
        icon="ri-file-text-line"
        title="No templates yet"
        text={
          channels.length
            ? "Create one here, or sync to bring in the templates already in your Twilio account."
            : "Add a WhatsApp channel first."
        }
        action={newButton}
      >
        <div className="table-responsive">
          <Table className="table-sm align-middle mb-0">
            <thead className="text-muted">
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Language</th>
                <th>Message</th>
                <th>Approval</th>
                <th>Last synced</th>
                <th>Active</th>
                <th className="text-end">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const ch = channelById(t.MessagingChannel_Id);
                return (
                  <tr key={t.Id}>
                    <td>
                      <div className="fw-medium">{t.Name}</div>
                      {channels.length > 1 && ch && <div className="small text-muted">{channelName(ch)}</div>}
                    </td>
                    <td className="text-capitalize">{t.Category ? String(t.Category).toLowerCase() : "-"}</td>
                    <td>{t.Language || "-"}</td>
                    <td>
                      <div className="wa-settings__truncate" title={t.Message}>
                        {t.Message}
                      </div>
                    </td>
                    <td>
                      <ApprovalBadge template={t} />
                    </td>
                    <td>
                      <DateCell value={t.LastSyncedDateTime} empty="Never" />
                    </td>
                    <td>
                      <Switch
                        id={`wa-tpl-active-${t.Id}`}
                        checked={t.FlagActive === null || t.FlagActive === undefined || isOn(t.FlagActive)}
                        disabled={togglingId === t.Id}
                        onChange={(v) => toggleActive(t, v)}
                      />
                    </td>
                    <td className="text-end text-nowrap">
                      <Button color="light" size="sm" className="me-1" title="Edit" onClick={() => setEditing(t)}>
                        <i className="ri-pencil-fill align-bottom" />
                      </Button>
                      <Button color="light" size="sm" title="Delete" onClick={() => setDeleting(t)}>
                        <i className="ri-delete-bin-line align-bottom text-danger" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </ListState>

      <NewTemplateModal
        open={creating}
        channels={channels}
        mergeFields={mergeFields}
        onClose={() => setCreating(false)}
        onSaved={() => {
          setCreating(false);
          load();
        }}
      />

      <EditTemplateModal
        row={editing}
        mergeFields={mergeFields}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />

      <DeleteModal
        show={!!deleting}
        loading={deleteBusy}
        onCloseClick={() => setDeleting(null)}
        onDeleteClick={confirmDelete}
        customText={`Delete the template "${deleting?.Name || ""}"? This also deletes it in Twilio.`}
      />
    </>
  );
};

TemplatesTab.propTypes = { channels: PropTypes.array.isRequired };

export default TemplatesTab;
